import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import { createPortal } from 'react-dom';
import {KeyConstant} from '../../utils/constants/keys.constants';
import './style.scss';

export default function CustomFilterSelect({
  optionsList,
  placeholder,
  setKidsGroup,
  valueEdit,
  edit,
  addInfo,
  keyName,
  searchable = false,
  searchPlaceholder = 'Buscar...',
  disabled = false,
  multiple = false,
  selectedValues = [],
  dropdownClassName = ''
}) {
    // STATES
    const [filterSelectOpen, setFilterSelectOpen] = useState(false);
    const [selectName, setSelectName] = useState(placeholder);
    const [searchTerm, setSearchTerm] = useState('');
    const [dropdownStyles, setDropdownStyles] = useState(null);
    const containerRef = useRef(null);
    const buttonRef = useRef(null);
    const optionsRef = useRef(null);

    function normalizeText(value) {
        return String(value || '')
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');
    }

    const filteredOptions = useMemo(() => {
        const allOptions = Array.isArray(optionsList) ? optionsList : [];
        if (!searchable) return allOptions;

        const term = normalizeText(searchTerm.trim());
        if (!term) return allOptions;

        return allOptions.filter((optionItem) => {
            const optionName = optionItem?.name ?? optionItem?.value ?? '';
            return normalizeText(optionName).includes(term);
        });
    }, [optionsList, searchable, searchTerm]);

    // METHODS
    function handleOpen() {
        if (disabled) return;
        if (!filterSelectOpen) {
            setDropdownStyles(null);
        }
        setFilterSelectOpen(!filterSelectOpen);
    }

    function closeOptions() {
        setFilterSelectOpen(false);
        setSearchTerm('');
        setDropdownStyles(null);
    }

    const updateDropdownPosition = useCallback(() => {
        const buttonElement = buttonRef.current;
        if (!buttonElement) return;

        const rect = buttonElement.getBoundingClientRect();
        const viewportPadding = 12;
        const offset = 8;
        const optionHeight = window.innerWidth <= 768 ? 49 : 54;
        const estimatedHeight = Math.min(
            210,
            filteredOptions.length * optionHeight + (searchable ? 60 : 2)
        );
        const availableBelow = window.innerHeight - rect.bottom - viewportPadding;
        const availableAbove = rect.top - viewportPadding;
        const shouldOpenUpwards =
            availableBelow < estimatedHeight && availableAbove > availableBelow;
        const availableHeight = shouldOpenUpwards
            ? availableAbove
            : availableBelow;
        const maxHeight = Math.max(
            80,
            Math.min(210, availableHeight - offset)
        );
        const width = Math.max(rect.width, 180);
        const left = Math.min(
            Math.max(viewportPadding, rect.left),
            window.innerWidth - viewportPadding - width
        );
        const verticalPosition = shouldOpenUpwards
            ? {
                  top: 'auto',
                  bottom: `${window.innerHeight - rect.top + offset}px`
              }
            : {
                  top: `${rect.bottom + offset}px`,
                  bottom: 'auto'
              };

        setDropdownStyles({
            ...verticalPosition,
            left: `${left}px`,
            width: `${width}px`,
            maxHeight: `${maxHeight}px`
        });
    }, [filteredOptions.length, searchable]);

    function chooseOption(optionItem) {
        const optionName = optionItem?.name ?? optionItem?.value ?? '';
        const optionId = optionItem?.id;
        if (multiple) {
            const currentValues = Array.isArray(selectedValues)
                ? selectedValues.map(value => String(value))
                : [];
            const normalizedOptionId = String(optionId ?? '');
            const clearsSelection = normalizedOptionId === '__all__' || !normalizedOptionId;
            const nextValues = clearsSelection
                ? []
                : currentValues.includes(normalizedOptionId)
                    ? currentValues.filter(value => value !== normalizedOptionId)
                    : [...currentValues, normalizedOptionId];
            const nextNames = optionsList
                .filter(option => nextValues.includes(String(option?.id ?? '')))
                .map(option => option?.name ?? option?.value ?? '');

            if (addInfo) {
                addInfo(keyName, nextValues, nextNames);
            }
            return;
        }
        setSelectName(optionName || placeholder);
        if (setKidsGroup) {
            if (optionName !== KeyConstant.SCHOOL_TYPE) {
                setKidsGroup(false);
            } else setKidsGroup(true);
        }
        if (addInfo) {
            addInfo(keyName, optionId, optionName);
        }
        closeOptions();
    }

    useEffect(() => {
        if (edit) {
            setSelectName(edit);
            return;
        }

        setSelectName(valueEdit || placeholder);
    }, [valueEdit, edit, placeholder]);

    useEffect(() => {
        if (!disabled) return;
        closeOptions();
    }, [disabled]);

    useLayoutEffect(() => {
        if (!filterSelectOpen) return;
        updateDropdownPosition();
    }, [filterSelectOpen, updateDropdownPosition]);

    useEffect(() => {
        if (!filterSelectOpen) return;

        function handleOutsideClick(event) {
            const target = event.target;
            if (
                buttonRef.current?.contains(target) ||
                optionsRef.current?.contains(target) ||
                containerRef.current?.contains(target)
            ) {
                return;
            }

            closeOptions();
        }

        function handleEscape(event) {
            if (event.key === 'Escape') {
                closeOptions();
            }
        }

        function syncDropdownPosition() {
            updateDropdownPosition();
        }

        document.addEventListener('mousedown', handleOutsideClick);
        document.addEventListener('keydown', handleEscape);
        window.addEventListener('resize', syncDropdownPosition);
        window.addEventListener('scroll', syncDropdownPosition, true);

        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
            document.removeEventListener('keydown', handleEscape);
            window.removeEventListener('resize', syncDropdownPosition);
            window.removeEventListener('scroll', syncDropdownPosition, true);
        };
    }, [filterSelectOpen, updateDropdownPosition]);

    const dropdownContent = filterSelectOpen && dropdownStyles ? (
        <div
            className={`options-container options-container--portal ${dropdownClassName}`.trim()}
            ref={optionsRef}
            style={dropdownStyles}
        >
            {searchable && (
                <div className='options-search'>
                    <input
                        type='text'
                        value={searchTerm}
                        placeholder={searchPlaceholder}
                        onChange={(event) => setSearchTerm(event.target.value)}
                    />
                </div>
            )}

            {filteredOptions.length > 0 ? (
                filteredOptions.map((optionItem, i) => (
                    <button
                        className={`option${
                            multiple && selectedValues.map(String).includes(String(optionItem?.id))
                                ? ' option--selected'
                                : ''
                        }`}
                        key={optionItem?.id ?? i}
                        onClick={() => {
                            chooseOption(optionItem);
                        }}
                        type='button'>
                        <span className='option__label'>
                            {optionItem?.name ?? optionItem?.value ?? ''}
                        </span>
                        {multiple &&
                          selectedValues.map(String).includes(String(optionItem?.id)) && (
                            <span className='material-symbols-rounded' aria-hidden='true'>
                                check
                            </span>
                        )}
                    </button>
                ))
            ) : (
                <p className='option option--empty'>Sin resultados</p>
            )}
        </div>
    ) : null;

    return (
        <div className='custom-filters-container' ref={containerRef}>
            <button
                className='first-option'
                type='button'
                onClick={handleOpen}
                disabled={disabled}
                ref={buttonRef}
            >
                <p>{selectName}</p>

                {filterSelectOpen ? (
                    <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='14.922'
                        height='8.875'
                        viewBox='0 0 14.922 8.875'>
                        <g
                            id='Arrow-Right'
                            transform='translate(-8.586 14.461) rotate(-90)'>
                            <path
                                id='Trazado_585'
                                data-name='Trazado 585'
                                d='M0,0,6.047,6.047,12.094,0'
                                transform='translate(7 22.094) rotate(-90)'
                                fill='none'
                                stroke='#909090'
                                strokeLinecap='round'
                                strokeWidth='2'
                            />
                        </g>
                    </svg>
                ) : (
                    <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='14.922'
                        height='8.875'
                        viewBox='0 0 14.922 8.875'>
                        <g id='Arrow-Right' transform='translate(23.508 -5.586) rotate(90)'>
                            <path
                                id='Trazado_585'
                                className='path2'
                                data-name='Trazado 585'
                                d='M0,0,6.047,6.047,12.094,0'
                                transform='translate(7 22.094) rotate(-90)'
                                fill='none'
                                stroke='#909090'
                                strokeLinecap='round'
                                strokeWidth='2'
                            />
                        </g>
                    </svg>
                )}
            </button>
            {filterSelectOpen && typeof document !== 'undefined'
                ? createPortal(dropdownContent, document.body)
                : null}
        </div>
    );
}
